import type { Fecha, Instante, Monto } from '../api/tipos';

/**
 * FORMATO DE PRESENTACIÓN
 * =======================
 *
 * La base guarda el dato; acá se decide cómo se lee. Dos reglas que no se negocian:
 *
 *  1. LA PLATA NUNCA PASA POR `Number`. Llega como texto (BigInt en guaraníes) y se formatea
 *     recorriendo los dígitos. Para sumar se usa BigInt. `Number('9007199254740993')` ya
 *     miente, y acá el que miente es un comprobante firmado.
 *  2. LOS INSTANTES SE MUESTRAN EN LA ZONA DE LA ENTIDAD, no en la del navegador de quien
 *     mira. Un pago hecho a las 19:50 en Asunción no puede leerse "mañana" porque el que
 *     audita abrió la pantalla desde Madrid.
 */

const SIMBOLO: Record<string, string> = { PYG: 'Gs.', USD: 'US$', BRL: 'R$', ARS: '$' };

/** Cuántos decimales tiene la moneda. El guaraní no tiene. */
function decimalesDe(moneda: string): number {
  return moneda === 'PYG' ? 0 : 2;
}

/** Puntos de miles sobre los dígitos, sin tocar el valor. */
function separarMiles(digitos: string): string {
  let salida = '';
  for (let i = 0; i < digitos.length; i += 1) {
    const restantes = digitos.length - i;
    salida += digitos[i];
    if (restantes > 1 && (restantes - 1) % 3 === 0) salida += '.';
  }
  return salida;
}

/** Formatea un monto que viene como texto. `null` se lee como raya, no como cero. */
export function monto(valor: Monto | null | undefined, moneda = 'PYG', opciones: { simbolo?: boolean } = {}): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  const negativo = valor.trim().startsWith('-');
  const digitos = valor.replace(/\D/g, '') || '0';
  const decimales = decimalesDe(moneda);
  let texto: string;
  if (decimales === 0) {
    texto = separarMiles(digitos);
  } else {
    const relleno = digitos.padStart(decimales + 1, '0');
    const entero = relleno.slice(0, relleno.length - decimales);
    const fraccion = relleno.slice(relleno.length - decimales);
    texto = `${separarMiles(entero)},${fraccion}`;
  }
  const signo = negativo && digitos !== '0' ? '-' : '';
  return opciones.simbolo === false ? `${signo}${texto}` : `${SIMBOLO[moneda] ?? moneda} ${signo}${texto}`;
}

/** Montos grandes en el tablero: "2,7 M". Solo para leer de un vistazo, nunca para operar. */
export function montoCorto(valor: Monto | null | undefined, moneda = 'PYG'): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  const digitos = valor.replace(/\D/g, '') || '0';
  const numero = BigInt(digitos);
  const MIL = 1000n;
  const MILLON = 1_000_000n;
  const MIL_MILLONES = 1_000_000_000n;
  if (numero >= MIL_MILLONES) return `${conUnDecimal(numero, MIL_MILLONES)} mil M`;
  if (numero >= MILLON) return `${conUnDecimal(numero, MILLON)} M`;
  if (numero >= 10n * MIL) return `${separarMiles((numero / MIL).toString())} mil`;
  return monto(digitos, moneda, { simbolo: false });
}

function conUnDecimal(numero: bigint, unidad: bigint): string {
  const entero = numero / unidad;
  const decimal = (numero % unidad) / (unidad / 10n);
  return decimal === 0n ? entero.toString() : `${entero},${decimal}`;
}

/** Lee lo que tipeó la persona ("1.250.000", "Gs. 1250000") como monto entero en texto. */
export function leerMonto(texto: string): Monto | null {
  const digitos = texto.replace(/\D/g, '');
  if (!digitos) return null;
  // Se normaliza para que "0001500" y "1500" sean el mismo monto.
  return BigInt(digitos).toString();
}

/** Suma montos con BigInt. Es la única forma de sumar plata en este front. */
export function sumar(...valores: Array<Monto | null | undefined>): Monto {
  return valores
    .reduce((total, valor) => total + BigInt(valor && valor.replace(/\D/g, '') ? valor.replace(/\D/g, '') : '0'), 0n)
    .toString();
}

export function restar(a: Monto, b: Monto): Monto {
  return (BigInt(a.replace(/\D/g, '') || '0') - BigInt(b.replace(/\D/g, '') || '0')).toString();
}

/** -1, 0 o 1. Comparar montos como texto ("90" < "100" es falso en texto) es un bug clásico. */
export function comparar(a: Monto, b: Monto): number {
  const x = BigInt(a.replace(/\D/g, '') || '0');
  const y = BigInt(b.replace(/\D/g, '') || '0');
  return x === y ? 0 : x < y ? -1 : 1;
}

export function esCero(valor: Monto | null | undefined): boolean {
  return !valor || BigInt(valor.replace(/\D/g, '') || '0') === 0n;
}

/**
 * Proporción entre dos montos, de 0 a 1, para el ancho de una barra. Se calcula con BigInt y
 * recién al final se convierte: lo que se pasa a `Number` es un porcentaje, nunca plata.
 */
export function proporcion(parte: Monto | null | undefined, total: Monto | null | undefined): number {
  const arriba = BigInt((parte ?? '0').replace(/\D/g, '') || '0');
  const abajo = BigInt((total ?? '0').replace(/\D/g, '') || '0');
  if (abajo === 0n) return 0;
  return Number((arriba * 10000n) / abajo) / 10000;
}

// --- Fechas e instantes ------------------------------------------------------------

export function fecha(valor: Fecha | null | undefined): string {
  if (!valor) return '—';
  const [anio, mes, dia] = valor.slice(0, 10).split('-');
  return `${dia}/${mes}/${anio}`;
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MESES_LARGOS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export function fechaCorta(valor: Fecha): string {
  const [, mes, dia] = valor.split('-');
  return `${Number(dia)} ${MESES[Number(mes) - 1]}`;
}

/** "15 de octubre de 2026", como se completa a mano en un comprobante. */
export function fechaEnPalabras(valor: Fecha): string {
  const [anio, mes, dia] = valor.split('-').map(Number);
  return `${dia} de ${MESES_LARGOS[(mes ?? 1) - 1]} de ${anio}`;
}

export function instante(valor: Instante | null | undefined, zona: string): string {
  if (!valor) return '—';
  return new Intl.DateTimeFormat('es-PY', {
    timeZone: zona,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(valor));
}

/** Solo la hora, en la zona de la entidad: "14:32". */
export function hora(valor: Instante | null | undefined, zona: string): string {
  if (!valor) return '—';
  return new Intl.DateTimeFormat('es-PY', { timeZone: zona, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(valor));
}

/** La hora del día en la zona de la entidad, para saludar. */
export function horaDelDia(zona: string): number {
  return Number(new Intl.DateTimeFormat('es-PY', { timeZone: zona, hour: 'numeric', hourCycle: 'h23' }).format(new Date()));
}

export function iniciales(nombre: string): string {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0]!.toUpperCase())
    .join('');
}

export function cantidad(numero: number, singular: string, plural: string): string {
  return `${numero.toLocaleString('es-PY')} ${numero === 1 ? singular : plural}`;
}

// --- Monto en letras ---------------------------------------------------------------

const UNIDADES = [
  'cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve', 'diez',
  'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve',
  'veinte', 'veintiuno', 'veintidós', 'veintitrés', 'veinticuatro', 'veinticinco', 'veintiséis',
  'veintisiete', 'veintiocho', 'veintinueve',
];
const DECENAS = ['', '', '', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const CIENTOS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];
const NOMBRE_MONEDA: Record<string, string> = { PYG: 'guaraníes', USD: 'dólares', BRL: 'reales', ARS: 'pesos' };

function hasta999(numero: number): string {
  if (numero === 100) return 'cien';
  const cientos = Math.floor(numero / 100);
  const resto = numero % 100;
  const partes: string[] = [];
  if (cientos > 0) partes.push(CIENTOS[cientos]!);
  if (resto > 0) {
    if (resto < 30) partes.push(UNIDADES[resto]!);
    else {
      const decena = Math.floor(resto / 10);
      const unidad = resto % 10;
      partes.push(unidad > 0 ? `${DECENAS[decena]} y ${UNIDADES[unidad]}` : DECENAS[decena]!);
    }
  }
  return partes.join(' ');
}

/**
 * El monto en palabras para el comprobante: es lo que hace que no se le pueda agregar un cero
 * después de firmado. Se calcula sobre los DÍGITOS con BigInt, nunca sobre un `Number`.
 *
 * El guaraní no tiene decimales, así que no hay centavos que escribir. Si algún día una entidad
 * cobra en otra moneda, esto escribe la parte entera y la fracción se muestra en cifras.
 */
export function montoEnLetras(valor: Monto | null | undefined, moneda = 'PYG'): string {
  const digitos = (valor ?? '').replace(/\D/g, '') || '0';
  const numero = BigInt(digitos);
  const nombre = NOMBRE_MONEDA[moneda] ?? moneda;
  if (numero === 0n) return `cero ${nombre}`;

  const grupos: number[] = [];
  let resto = numero;
  while (resto > 0n) {
    grupos.push(Number(resto % 1000n));
    resto /= 1000n;
  }

  const partes: string[] = [];
  for (let i = grupos.length - 1; i >= 0; i -= 1) {
    const grupo = grupos[i]!;
    if (grupo === 0) continue;
    // "veintiún mil", no "veintiuno mil": delante de la escala, `uno` apocopa.
    const texto = i === 0 ? hasta999(grupo) : hasta999(grupo).replace(/\buno$/, 'ún');
    if (i === 0) partes.push(texto);
    else if (i === 1) partes.push(grupo === 1 ? 'mil' : `${texto} mil`);
    else if (i === 2) partes.push(grupo === 1 ? 'un millón' : `${texto} millones`);
    else partes.push(grupo === 1 ? 'mil millones' : `${texto} mil millones`);
  }
  return `${partes.join(' ')} ${nombre}`;
}
